// types

import type {Primitive} from 'react-hook-form';

export const isStr = (x: any): x is string =>
  typeof x === 'string';

export const isNum = (x: any): x is number =>
  typeof x === 'number';

export const isBool = (x: any): x is boolean =>
  typeof x === 'boolean';

export const isObj = (x: unknown): x is Record<string, unknown> => {
  if (Object.prototype.toString.call(x) !== '[object Object]') {
    return false;
  }

  const prototype = Object.getPrototypeOf(x);

  return prototype === null || prototype === Object.prototype;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const isFunc = (x: any): x is Function =>
  typeof x === 'function';

export const isNull = (x: unknown): x is null =>
  x === null;

export const isUndefined = (x: unknown): x is undefined =>
  typeof x === 'undefined';

export const isNil = (x: unknown): x is (null | undefined) =>
  isNull(x) || isUndefined(x);

// fast

export const uniq = <T>(arr: T[]) => [...new Set(arr)];

export const ensureArray = <T>(maybeArr: T | T[]) => (
  Array.isArray(maybeArr) ? maybeArr : [maybeArr]
);

// eslint-disable-next-line @typescript-eslint/ban-types
export const mapObject = <T extends object, U>(
  mappingFn: (value: T[keyof T]) => U,
  obj: T,
): Record<keyof T, U> => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const newObj = {} as Record<keyof T, U>;

  for (const i in obj) {
    if (obj.hasOwnProperty(i)) {
      const oldValue = obj[i];
      newObj[i] = mappingFn(oldValue);
    }
  }

  return newObj;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export const mapObjectAsync = async <T extends object, U>(
  mappingFn: (value: T[keyof T]) => Promise<U>,
  obj: T,
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const newObj = {} as Record<keyof T, U>;

  for (const i in obj) {
    if (obj.hasOwnProperty(i)) {
      const oldValue = obj[i];
      newObj[i] = await mappingFn(oldValue);
    }
  }

  return newObj;
};

// language

export const possessive = (noun: string) => (
  noun.slice(-1).toLowerCase() === 's' ? `${noun}'` : `${noun}'s`
);

// jsx

type StyleVars = (vars: Record<string, Primitive>) => Record<string, string>;

export const styleVars: StyleVars = (vars) =>
  Object.keys(vars).reduce((acc, key) => {
    const val = vars[key];

    if (!isNil(val)) {
      acc[`--${key}`] = val.toString().trim();
    }

    return acc;
  }, {});
